import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { list as configList } from '@/graph/config';
import {
  getProcedureDelayReason,
  isDelayReasonRequired,
  proceduresForOperationRoomDay,
  submitDelayReason,
  updateDelayReason,
} from '@/graph/procedureDelayReason';
import { ConfigType } from '@/types/HospitalConfig';
import {
  configuredProcedureDelayReasons,
  formatProcedureScheduleDate,
  formatStoredProcedureDelayReason,
  isFirstScheduledProcedure,
  isProcedureDelayEnabled,
  parseStoredProcedureDelayReason,
  procedureDelayReasonLabel,
  resolveDelayStartedAt,
} from './procedureDelay';

type UseProcedureDelayReasonArgs = {
  procedureId?: number | string | null;
  patientId?: number | string | null;
  operationRoomId?: number | string | null;
  startTime?: string | null;
};

const POLL_MS = 20000;

export function useProcedureDelayReason({
  procedureId,
  patientId,
  operationRoomId,
  startTime,
}: UseProcedureDelayReasonArgs) {
  const { data: delayReasonConfigData } = useQuery(configList, {
    variables: { type: ConfigType.ProcedureDelayReason },
  });

  const configuredDelayReasons = useMemo(
    () => configuredProcedureDelayReasons(delayReasonConfigData?.configs),
    [delayReasonConfigData?.configs]
  );

  const { data: delayRequiredData, loading: delayLoading } = useQuery(isDelayReasonRequired, {
    variables: { procedureId, operationRoomId },
    skip: !procedureId || !operationRoomId,
    fetchPolicy: 'network-only',
    pollInterval: POLL_MS,
  });

  const {
    data: existingDelayReasonData,
    loading: existingLoading,
    refetch: refetchExistingDelayReason,
  } = useQuery(getProcedureDelayReason, {
    variables: { procedureId },
    skip: !procedureId,
    fetchPolicy: 'network-only',
  });

  const scheduleDate = formatProcedureScheduleDate(startTime);
  const { data: scheduleData, loading: scheduleLoading } = useQuery(proceduresForOperationRoomDay, {
    variables: { date: scheduleDate, operationRoomId, isCanceled: false },
    skip: !operationRoomId,
    fetchPolicy: 'network-only',
    pollInterval: POLL_MS,
  });

  const delayInfo = delayRequiredData?.isDelayReasonRequired;
  const existing = existingDelayReasonData?.procedureDelayReason ?? null;
  const parsedReason = parseStoredProcedureDelayReason(existing?.reason);
  const isFirstCaseOfDay = isFirstScheduledProcedure(procedureId, scheduleData?.procedures ?? []);
  const isDelayed = isProcedureDelayEnabled({
    delayStartedAt: delayInfo?.delayStartedAt,
    isFirstCaseOfDay,
    delayReasonRequired: Boolean(delayInfo?.required),
  });

  const delaySettled = !procedureId || !operationRoomId || !delayLoading || Boolean(delayRequiredData);
  const scheduleSettled = !operationRoomId || !scheduleLoading || Boolean(scheduleData);
  const existingSettled = !procedureId || !existingLoading || Boolean(existingDelayReasonData);
  const ready = delaySettled && scheduleSettled && existingSettled;

  const [submit, { loading: submitting }] = useMutation(submitDelayReason);
  const [update, { loading: updating }] = useMutation(updateDelayReason);

  const saveReason = useCallback(
    async (reasonId: string, otherDescription?: string) => {
      const reasonValue = formatStoredProcedureDelayReason(reasonId, otherDescription);
      if (existing?.id) {
        await update({ variables: { id: existing.id, reason: reasonValue } });
      } else {
        const delayStartedAt = resolveDelayStartedAt({
          serverDelayStartedAt: delayInfo?.delayStartedAt,
          procedureStartTime: startTime,
        });
        if (!delayStartedAt) {
          throw new Error('Cannot record a delay reason without a delay start time');
        }
        await submit({
          variables: {
            procedureId,
            patientId,
            operationRoomId,
            reason: reasonValue,
            delayStartedAt,
          },
        });
      }
      await refetchExistingDelayReason();
    },
    [
      delayInfo?.delayStartedAt,
      existing?.id,
      operationRoomId,
      patientId,
      procedureId,
      refetchExistingDelayReason,
      startTime,
      submit,
      update,
    ]
  );

  return {
    ready,
    isDelayed,
    existingLoading,
    existing,
    hasRecordedReason: Boolean(existing?.reason),
    reasonLabel: procedureDelayReasonLabel(existing?.reason, configuredDelayReasons),
    existingReasonId: parsedReason?.id ?? null,
    existingOtherDescription: parsedReason?.description ?? null,
    configuredDelayReasons,
    delayInfo,
    delayDurationSeconds: delayInfo?.elapsedSeconds ?? 0,
    saveReason,
    saving: submitting || updating,
  };
}
